import { useState } from 'react';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Input, Spinner, Textarea } from '@fluentui/react-components';
import type { QcTemplate } from '@qcbot/shared';
import { createQcTemplate, generateQcTemplateRules, importQcTemplateRules } from '../api';
import { getDocumentType } from '../officeApi';

const MAX_NAME_LENGTH = 100;
const MIN_PROMPT_LENGTH = 8;
const MAX_PROMPT_LENGTH = 40000;

/**
 * Quick-onboarding QC template creation, directly from the task pane. Deliberately narrower than
 * the admin app's "Create with AI" dialog: no per-row review table (rules + form fields +
 * rule-linking) — that's a lot of UI for a ~320px sidepane. Generated rules are imported in full
 * automatically and summarized afterward; anyone who wants row-by-row control, form fields, or to
 * fine-tune severities can do that later in the admin panel.
 */
export function NewTemplateDialog({
  open,
  onClose,
  onCreated,
  projectId,
  isFirstTemplate,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (template: QcTemplate) => void;
  projectId: string;
  isFirstTemplate: boolean;
}) {
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [phase, setPhase] = useState<'form' | 'generating' | 'done'>('form');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rulesAdded, setRulesAdded] = useState(0);
  const [createdTemplate, setCreatedTemplate] = useState<QcTemplate | null>(null);

  function reset() {
    setName('');
    setPrompt('');
    setPhase('form');
    setError(null);
    setRulesAdded(0);
    setCreatedTemplate(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function handleDone() {
    if (createdTemplate) onCreated(createdTemplate);
    reset();
    onClose();
  }

  async function handleCreateBlank() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setError(null);
    try {
      const template = await createQcTemplate({ projectId, name: trimmed, isDefault: isFirstTemplate, documentTypes: [getDocumentType()] });
      reset();
      onCreated(template);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create template');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleGenerate() {
    const trimmedName = name.trim();
    const trimmedPrompt = prompt.trim();
    if (!trimmedName || trimmedPrompt.length < MIN_PROMPT_LENGTH) return;
    setSubmitting(true);
    setPhase('generating');
    setError(null);
    try {
      const template = await createQcTemplate({ projectId, name: trimmedName, isDefault: isFirstTemplate, documentTypes: [getDocumentType()] });
      const rules = await generateQcTemplateRules(template.id, trimmedPrompt);
      const created = rules.length > 0 ? await importQcTemplateRules(template.id, rules) : [];
      setCreatedTemplate(template);
      setRulesAdded(created.length);
      setPhase('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to generate rules');
      setPhase('form');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface style={{ margin: 16 }}>
        <DialogBody>
          <DialogTitle>New QC template</DialogTitle>
          <DialogContent>
            {phase === 'done' ? (
              <div style={{ fontSize: 13, color: '#2e7d32' }}>
                ✓ Created &quot;{createdTemplate?.name}&quot;{rulesAdded > 0 ? ` with ${rulesAdded} QC rule${rulesAdded === 1 ? '' : 's'}.` : '.'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Field label="Name" required>
                  <Input
                    value={name}
                    maxLength={MAX_NAME_LENGTH}
                    disabled={phase === 'generating'}
                    onChange={(_, d) => setName(d.value)}
                    placeholder="e.g. Diabetes HCP Awareness QC"
                  />
                </Field>
                <Field label="Describe what this template should check for">
                  <Textarea
                    rows={5}
                    maxLength={MAX_PROMPT_LENGTH}
                    disabled={phase === 'generating'}
                    value={prompt}
                    onChange={(_, d) => setPrompt(d.value)}
                    placeholder='e.g. "A market research deck on diabetes treatment awareness in HCPs — check for low base-size cautions, stat-sig footnotes, and consistent brand terminology."'
                  />
                </Field>
                {phase === 'generating' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#666' }}>
                    <Spinner size="tiny" /> Generating rules — this can take up to a minute…
                  </div>
                )}
                {error && <div style={{ color: '#b91c1c', fontSize: 13 }}>{error}</div>}
                {phase !== 'generating' && (
                  <Button
                    appearance="transparent"
                    size="small"
                    style={{ alignSelf: 'flex-start', padding: 0, minWidth: 0, fontSize: 12, textDecoration: 'underline' }}
                    disabled={!name.trim() || submitting}
                    onClick={() => void handleCreateBlank()}
                  >
                    Or create a blank template (no AI)
                  </Button>
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            {phase === 'done' ? (
              <Button appearance="primary" onClick={handleDone}>
                Done
              </Button>
            ) : (
              <>
                <Button appearance="secondary" disabled={submitting} onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  disabled={!name.trim() || prompt.trim().length < MIN_PROMPT_LENGTH || submitting}
                  onClick={() => void handleGenerate()}
                >
                  {phase === 'generating' ? 'Generating…' : 'Generate QC rules'}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
